import jsPDF from 'jspdf';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Extend dayjs with advancedFormat
dayjs.extend(advancedFormat);

// Extend dayjs with advancedFormat
dayjs.extend(advancedFormat);

// Utility function to format addresses for PDF (similar to fullAddress but returns string)
const formatAddressForPDF = (address) => {
  if (!address) return null;

  const parts = [
    address?.current_address_line_1,
    address?.current_address_line_2,
    address?.current_city,
    address?.current_state,
    address?.current_country,
    address?.current_zipcode,
  ].filter(Boolean); // Remove undefined, null, or empty values

  return parts.join(", ");
};

// Utility function to format dates for PDF
const formatDateForPDF = (date, officeData = null) => {
  if (!date) return 'N/A';
  
  // Determine the date format from office settings
  const officeDateFormat = officeData?.date_format === 'en-US' 
    ? 'MM/DD/YYYY' 
    : officeData?.date_format === 'en-GB' 
      ? 'DD/MM/YYYY' 
      : 'YYYY/MM/DD';

  return dayjs(date).format(officeDateFormat);
};


export const generateLabOrderPDF = async (labOrder, patientInfo = null, officeData = null, preferredLab = null, providerData = null) => {
  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const lineHeight = 7;
  let yPosition = margin;

  // Convert single order to array for consistent processing
  const orders = Array.isArray(labOrder) ? labOrder : [labOrder];

  // Header
  pdf.setFontSize(18);
  pdf.setTextColor(41, 128, 185);
  pdf.text(`Order ID - ${labOrder.lab_id || labOrder.id}`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 2;

 



  // Facility and Lab Information Section
  if (officeData || preferredLab) {
    // Check if we need a new page
    if (yPosition > pageHeight - 100) {
      pdf.addPage();
      yPosition = margin;
    }

    const sectionStartY = yPosition;
    const leftColumnX = margin;
    const rightColumnX = pageWidth / 2 + 10;
    const columnWidth = (pageWidth - margin * 2 - 20) / 2;

    // Clinic Information (Left Column)
    if (officeData) {
      pdf.setFontSize(12);
      pdf.setTextColor(52, 73, 94);
      pdf.text('Facility Information', leftColumnX, yPosition);
      yPosition += lineHeight;

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);

      // Helper function to check if data should be displayed
      const shouldDisplay = (data) => Array.isArray(data) && data[1] === true;

      if (officeData.facility_name && officeData.facility_name[0]) {
        pdf.text(`Name: ${officeData.facility_name[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldDisplay(officeData.facility_address)) {
        const addressLines = pdf.splitTextToSize(`Address: ${officeData.facility_address[0]}`, columnWidth);
        addressLines.forEach(line => {
          pdf.text(line, leftColumnX, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (shouldDisplay(officeData.city) || shouldDisplay(officeData.state) || shouldDisplay(officeData.zipcode)) {
        const cityStateZip = [];
        if (shouldDisplay(officeData.city)) cityStateZip.push(officeData.city[0]);
        if (shouldDisplay(officeData.state)) cityStateZip.push(officeData.state[0]);
        if (shouldDisplay(officeData.zipcode)) cityStateZip.push(officeData.zipcode[0]);
        
        if (cityStateZip.length > 0) {
          pdf.text(cityStateZip.join(', '), leftColumnX, yPosition);
          yPosition += lineHeight * 0.8;
        }
      }

      if (shouldDisplay(officeData.office_phone)) {
        pdf.text(`Phone: ${officeData.office_phone[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldDisplay(officeData.office_fax)) {
        pdf.text(`Fax: ${officeData.office_fax[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldDisplay(officeData.buisness_email)) {
        pdf.text(`Email: ${officeData.buisness_email[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }
    }

    // Reset yPosition for right column
    let rightColumnY = sectionStartY;

    // Preferred Lab Information (Right Column)
    if (preferredLab && preferredLab.length > 0) {
      const labInfo = preferredLab[0];
      
      pdf.setFontSize(12);
      pdf.setTextColor(52, 73, 94);
      pdf.text('Preferred Laboratory', rightColumnX, rightColumnY);
      rightColumnY += lineHeight;

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);

      if (labInfo.name) {
        pdf.text(`Name: ${labInfo.name}`, rightColumnX, rightColumnY);
        rightColumnY += lineHeight * 0.8;
      }

      if (labInfo.address) {
        const addressLines = pdf.splitTextToSize(`Address: ${labInfo.address}`, columnWidth);
        addressLines.forEach(line => {
          pdf.text(line, rightColumnX, rightColumnY);
          rightColumnY += lineHeight * 0.8;
        });
      }

      if (labInfo.phone) {
        pdf.text(`Phone: ${labInfo.phone}`, rightColumnX, rightColumnY);
        rightColumnY += lineHeight * 0.8;
      }

      if (labInfo.fax) {
        pdf.text(`Fax: ${labInfo.fax}`, rightColumnX, rightColumnY);
        rightColumnY += lineHeight * 0.8;
      }

      if (labInfo.email) {
        pdf.text(`Email: ${labInfo.email}`, rightColumnX, rightColumnY);
        rightColumnY += lineHeight * 0.8;
      }
    }

    // Set yPosition to the maximum of both columns
    yPosition = Math.max(yPosition, rightColumnY) + lineHeight;
  }

  // Patient Information Section
  if (patientInfo) {
    // Check if we need a new page
    if (yPosition > pageHeight - 80) {
      pdf.addPage();
      yPosition = margin;
    }

    // Add divider line above patient information
    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight;

    pdf.setFontSize(12);
    pdf.setTextColor(52, 73, 94);
    pdf.text('Patient Information', margin, yPosition);
    
    // Add order date on the right side
    if (orders.length > 0) {
      const orderDate = orders[0].date_added ? new Date(orders[0].date_added).toLocaleDateString() : orders[0].orderDate || 'N/A';
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Order Date: ${orderDate}`, pageWidth - margin, yPosition, { align: 'right' });
    }
    
    yPosition += lineHeight;

    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    // Patient Name
    const patientName = patientInfo.first_name && patientInfo.last_name 
      ? `${patientInfo.first_name} ${patientInfo.last_name}`
      : 'N/A';
    pdf.text(`Name: ${patientName}`, margin, yPosition);
    yPosition += lineHeight * 0.8;

    // Date of Birth and Age
    if (patientInfo.date_of_birth) {
      const dob = formatDateForPDF(patientInfo.date_of_birth, officeData);
      pdf.text(`DOB: ${dob}`, margin, yPosition);
      
      // Calculate age
      const today = new Date();
      const birthDate = new Date(patientInfo.date_of_birth);
      const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      pdf.text(`Age: ${age} y/o`, margin + 80, yPosition);
      yPosition += lineHeight * 0.8;
    }

    // Gender
    if (patientInfo.gender) {
      pdf.text(`Gender: ${patientInfo.gender}`, margin, yPosition);
      yPosition += lineHeight * 0.8;
    }

    // Address
    if (patientInfo.current_address_line_1 || patientInfo.current_city || patientInfo.current_state || patientInfo.current_zipcode) {
      // Create address object compatible with fullAddress function
      const addressObj = {
        current_address_line_1: patientInfo.current_address_line_1,
        current_address_line_2: patientInfo.current_address_line_2,
        current_city: patientInfo.current_city,
        current_state: patientInfo.current_state,
        current_country: patientInfo.current_country,
        current_zipcode: patientInfo.current_zipcode
      };
      
      const formattedAddress = formatAddressForPDF(addressObj);
      if (formattedAddress) {
        const addressLines = pdf.splitTextToSize(`Address: ${formattedAddress}`, pageWidth - margin * 2);
        addressLines.forEach(line => {
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }
    }

    // Phone
    if (patientInfo.patient_phone) {
      pdf.text(`Phone: ${patientInfo.patient_phone}`, margin, yPosition);
      yPosition += lineHeight * 0.8;
    }

    yPosition += lineHeight;

    // Draw separator line
    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight * 1.5;
  }

  // Orders Details
  orders.forEach((order, orderIndex) => {
    // Check if we need a new page
    if (yPosition > pageHeight - 80) {
      pdf.addPage();
      yPosition = margin;
    }

  


    // Order Information
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    
    // Laboratory Tests
    pdf.setFontSize(11);
    pdf.setTextColor(52, 73, 94);
    pdf.text('Laboratory Tests:', margin, yPosition);
    yPosition += lineHeight;
    
    // Handle lab_name array or fallback to tests array
    const tests = order.lab_name || order.tests || [];
    
    if (tests.length > 0) {
      tests.forEach((test, testIndex) => {
        // Check if we need a new page for each lab section
        if (yPosition > pageHeight - 80) {
          pdf.addPage();
          yPosition = margin;
        }
        
        // Lab Section Header
        pdf.setFontSize(10);
        pdf.setTextColor(41, 128, 185);
        const labNumber = testIndex + 1;
        pdf.text(`Lab ${labNumber}:`, margin, yPosition);
        yPosition += lineHeight;
        
        // Lab Name
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        const testName = test.lab_name || test.name || 'Unknown Test';
        pdf.text(`Name: ${testName}`, margin + 5, yPosition);
        
                 yPosition += lineHeight;
        
        // Lab Diagnosis
        if (test.lab_diagnosis) {
          pdf.setFontSize(9);
          pdf.setTextColor(178, 34, 34);
          pdf.text('Diagnosis:', margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
          
          pdf.setFontSize(9);
          pdf.setTextColor(0, 0, 0);
          // Handle long diagnosis text with word wrapping
          const diagnosisLines = pdf.splitTextToSize(test.lab_diagnosis, pageWidth - margin - 20);
          diagnosisLines.forEach((line) => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin + 10, yPosition);
            yPosition += lineHeight * 0.8;
          });
          yPosition += lineHeight * 0.3;
        }
        
        // Lab Instructions
        if (test.lab_instructions) {
          pdf.setFontSize(9);
          pdf.setTextColor(25, 135, 84);
          pdf.text('Instructions:', margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
          
          pdf.setFontSize(9);
          pdf.setTextColor(0, 0, 0);
          // Handle long instructions text with word wrapping
          const instructionLines = pdf.splitTextToSize(test.lab_instructions, pageWidth - margin - 20);
          instructionLines.forEach((line) => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin + 10, yPosition);
            yPosition += lineHeight * 0.8;
          });
          yPosition += lineHeight * 0.3;
        }
        
        // Show other category/description if available
        if (test.category || test.description) {
          pdf.setFontSize(8);
          pdf.setTextColor(128, 128, 128);
          const additionalInfo = [test.category, test.description].filter(Boolean).join(' - ');
          if (additionalInfo) {
            pdf.text(additionalInfo, margin + 5, yPosition);
            yPosition += lineHeight * 0.8;
          }
        }
        
        // Add separator line between labs (except for the last one)
        if (testIndex < tests.length - 1) {
          yPosition += lineHeight * 0.5;
          pdf.setDrawColor(220, 220, 220);
          pdf.line(margin + 5, yPosition, pageWidth - margin - 5, yPosition);
          yPosition += lineHeight;
        } else {
          yPosition += lineHeight;
        }
      });
    } else {
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text('No laboratory tests specified', margin + 5, yPosition);
      yPosition += lineHeight;
    }

    // Add spacing between orders
    if (orderIndex < orders.length - 1) {
      yPosition += lineHeight;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += lineHeight * 1.5;
    }
  });

  // Provider Information and Signature Section
  if (providerData) {
    // Check if we need a new page
    if (yPosition > pageHeight - 120) {
      pdf.addPage();
      yPosition = margin;
    }

    yPosition += lineHeight * 2;

    // Create two columns: Provider info (left) and Signature (right)
    const leftColumnX = margin;
    const rightColumnX = pageWidth / 2 + 20;
    const providerStartY = yPosition;

    // Provider Information (Left Column)
    pdf.setFontSize(12);
    pdf.setTextColor(52, 73, 94);
    pdf.text('Provider Information', leftColumnX, yPosition);
    yPosition += lineHeight;

    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    // Provider Name
    if (providerData.firstname && providerData.lastname) {
      const providerFullName = `${providerData.firstname} ${providerData.lastname}`;
      pdf.text(`Provider Name: ${providerFullName}`, leftColumnX, yPosition);
      yPosition += lineHeight * 0.8;
    }

    // Work Phone
    const workPhone = providerData?.personel_settings_info?.[0]?.contact?.work_phone;
    if (workPhone) {
      pdf.text(`Work Phone: ${workPhone}`, leftColumnX, yPosition);
      yPosition += lineHeight * 0.8;
    }

    // Medical Identifier Information
    const medicalInfo = providerData?.medical_identifer_info?.[0];
    if (medicalInfo) {
      const {
        primary_speciality,
        secondary_speciality,
        medical_license,
        state_license,
        provider_tax_id,
        taxonomy_code,
        dea_number,
        provisional_registration
      } = medicalInfo;

      // Helper function to check if data should be displayed
      const shouldShowInfo = (data) => Array.isArray(data) && data[1] === true;

      if (shouldShowInfo(primary_speciality)) {
        pdf.text(`Primary Speciality: ${primary_speciality[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldShowInfo(secondary_speciality)) {
        pdf.text(`Secondary Speciality: ${secondary_speciality[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldShowInfo(medical_license)) {
        pdf.text(`Medical License: ${medical_license[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldShowInfo(state_license)) {
        pdf.text(`State License: ${state_license[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldShowInfo(provisional_registration)) {
        pdf.text(`Provisional Registration: ${provisional_registration[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldShowInfo(provider_tax_id)) {
        pdf.text(`Provider Tax ID: ${provider_tax_id[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldShowInfo(taxonomy_code)) {
        pdf.text(`Taxonomy Code: ${taxonomy_code[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (shouldShowInfo(dea_number)) {
        pdf.text(`DEA Number: ${dea_number[0]}`, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }
    }

    // Signature Section (Right Column)
    let signatureY = providerStartY + lineHeight * 6; // Position signature lower
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    // Draw signature line
    const signatureLineLength = 80;
    pdf.line(rightColumnX, signatureY, rightColumnX + signatureLineLength, signatureY);
    
    // Add "Signature" label below the line
    signatureY += lineHeight;
    pdf.text('Signature', rightColumnX + (signatureLineLength / 2), signatureY, { align: 'center' });

    // Update yPosition to ensure proper spacing
    yPosition = Math.max(yPosition, signatureY + lineHeight);
  }

  // Add page numbers
  const pageCount = pdf.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // Save the PDF
  const dateStamp = new Date().toISOString().split('T')[0];
  let fileName;
  
  if (orders.length === 1) {
    const orderId = orders[0].lab_id || orders[0].id || 'Unknown';
    const patientName = patientInfo?.first_name && patientInfo?.last_name 
      ? `${patientInfo.first_name}_${patientInfo.last_name}`
      : orders[0].patientName?.replace(/\s+/g, '_') || 'Unknown_Patient';
    fileName = `Lab_Order_${orderId}_${patientName}_${dateStamp}.pdf`;
  } else {
    fileName = `Lab_Orders_Summary_${dateStamp}.pdf`;
  }
  
  pdf.save(fileName);
};

export const generateFaceSheetPDF = async (
  patientInfo = null,
  officeData = null,
  checkedItems = {},
  faceSheetData = {}
) => {
  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const lineHeight = 7;
  let yPosition = margin;

  // Header
  pdf.setFontSize(24);
  pdf.setTextColor(41, 128, 185);
  pdf.text('PATIENT FACE SHEET', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 2;

  // Facility Information Section (Left Side)
  if (officeData) {
    // Check if we need a new page
    if (yPosition > pageHeight - 100) {
      pdf.addPage();
      yPosition = margin;
    }

    const leftColumnX = margin;
    const columnWidth = (pageWidth - margin * 2) / 2;

    // Clinic Information (Left Column)
    pdf.setFontSize(12);
    pdf.setTextColor(52, 73, 94);
    pdf.text('Facility Information', leftColumnX, yPosition);
    const facilityStartY = yPosition;
    yPosition += lineHeight;

    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    // Helper function to check if data should be displayed
    const shouldDisplay = (data) => Array.isArray(data) && data[1] === true;

    if (officeData.facility_name && officeData.facility_name[0]) {
      pdf.text(`Name: ${officeData.facility_name[0]}`, leftColumnX, yPosition);
      yPosition += lineHeight * 0.8;
    }

    if (shouldDisplay(officeData.facility_address)) {
      const addressLines = pdf.splitTextToSize(`Address: ${officeData.facility_address[0]}`, columnWidth);
      addressLines.forEach(line => {
        pdf.text(line, leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      });
    }

    if (shouldDisplay(officeData.city) || shouldDisplay(officeData.state) || shouldDisplay(officeData.zipcode)) {
      const cityStateZip = [];
      if (shouldDisplay(officeData.city)) cityStateZip.push(officeData.city[0]);
      if (shouldDisplay(officeData.state)) cityStateZip.push(officeData.state[0]);
      if (shouldDisplay(officeData.zipcode)) cityStateZip.push(officeData.zipcode[0]);
      
      if (cityStateZip.length > 0) {
        pdf.text(cityStateZip.join(', '), leftColumnX, yPosition);
        yPosition += lineHeight * 0.8;
      }
    }

    if (shouldDisplay(officeData.office_phone)) {
      pdf.text(`Phone: ${officeData.office_phone[0]}`, leftColumnX, yPosition);
      yPosition += lineHeight * 0.8;
    }

    if (shouldDisplay(officeData.office_fax)) {
      pdf.text(`Fax: ${officeData.office_fax[0]}`, leftColumnX, yPosition);
      yPosition += lineHeight * 0.8;
    }

    if (shouldDisplay(officeData.buisness_email)) {
      pdf.text(`Email: ${officeData.buisness_email[0]}`, leftColumnX, yPosition);
      yPosition += lineHeight * 0.8;
    }

    yPosition += lineHeight;

    // Draw separator line
    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight * 1.5;
  }

  // Patient Information Header
  if (patientInfo) {
    pdf.setFontSize(14);
    pdf.setTextColor(52, 73, 94);
    pdf.text('PATIENT INFORMATION', margin, yPosition);
    yPosition += lineHeight;

    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight;

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    // Patient Name and basic info
    const patientName = patientInfo.first_name && patientInfo.last_name 
      ? `${patientInfo.first_name} ${patientInfo.last_name}`
      : 'N/A';
    pdf.text(`Patient Name: ${patientName}`, margin, yPosition);
    
    if (patientInfo.date_of_birth) {
      const dob = formatDateForPDF(patientInfo.date_of_birth, officeData);
      const today = new Date();
      const birthDate = new Date(patientInfo.date_of_birth);
      const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      pdf.text(`DOB: ${dob} (Age: ${age})`, margin + 90, yPosition);
    }
    yPosition += lineHeight;

    if (patientInfo.gender) {
      pdf.text(`Gender: ${patientInfo.gender}`, margin, yPosition);
    }
    
    pdf.text(`Report Generated: ${formatDateForPDF(new Date(), officeData)}`, margin + 90, yPosition);
    yPosition += lineHeight * 2;
  }

  // Insurance Information
  if (checkedItems.primaryInsurance && faceSheetData.insuranceData) {
    yPosition = addInsuranceSection(pdf, 'PRIMARY INSURANCE', faceSheetData.insuranceData, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  if (checkedItems.secondaryInsurance && faceSheetData.secondaryInsurance) {
    yPosition = addInsuranceSection(pdf, 'SECONDARY INSURANCE', faceSheetData.secondaryInsurance, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Allergies Section
  if (checkedItems.allergies && faceSheetData.patientAllergies) {
    yPosition = addAllergiesSection(pdf, faceSheetData.patientAllergies, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Patient Diagnosis Section
  if (checkedItems.patientDiagnosis && faceSheetData.patientDiagnosis) {
    yPosition = addDiagnosisSection(pdf, faceSheetData.patientDiagnosis, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Family History Section
  if (checkedItems.familyHistory && faceSheetData.familyHistory) {
    yPosition = addFamilyHistorySection(pdf, faceSheetData.familyHistory, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // All Medications Section
  if (checkedItems.allMedications && faceSheetData.allMedications) {
    yPosition = addMedicationsSection(pdf, faceSheetData.allMedications, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Past Medical History Section
  if (checkedItems.pastMedicalHistory && faceSheetData.pastMedicalHistory) {
    yPosition = addPastMedicalHistorySection(pdf, faceSheetData.pastMedicalHistory, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Surgical History Section
  if (checkedItems.surgicalHistory && faceSheetData.surgicalHistory) {
    yPosition = addSurgicalHistorySection(pdf, faceSheetData.surgicalHistory, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Social History Section
  if (checkedItems.socialHistory && faceSheetData.socialHistory) {
    yPosition = addSocialHistorySection(pdf, faceSheetData.socialHistory, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Vaccination History Section
  if (checkedItems.vaccinationHistory && faceSheetData.vaccinationHistory) {
    yPosition = addVaccinationHistorySection(pdf, faceSheetData.vaccinationHistory, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Patient Goals Section
  if (checkedItems.patientGoals && faceSheetData.patientGoals) {
    yPosition = addPatientGoalsSection(pdf, faceSheetData.patientGoals, margin, yPosition, pageWidth, pageHeight, lineHeight);
  }

  // Add page numbers
  const pageCount = pdf.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // Save the PDF
  const dateStamp = new Date().toISOString().split('T')[0];
  const patientName = patientInfo?.first_name && patientInfo?.last_name 
    ? `${patientInfo.first_name}_${patientInfo.last_name}`
    : 'Patient';
  const fileName = `FaceSheet_${patientName}_${dateStamp}.pdf`;
  
  pdf.save(fileName);
};

// Helper functions for each section
const addInsuranceSection = (pdf, title, insuranceData, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text(title, margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (insuranceData && insuranceData.length > 0) {
    const insurance = insuranceData[0];
    if (insurance.insurance_name) {
      pdf.text(`Insurance Name: ${insurance.insurance_name}`, margin, yPosition);
      yPosition += lineHeight * 0.8;
    }
    if (insurance.policy_number) {
      pdf.text(`Policy Number: ${insurance.policy_number}`, margin, yPosition);
      yPosition += lineHeight * 0.8;
    }
    if (insurance.group_number) {
      pdf.text(`Group Number: ${insurance.group_number}`, margin, yPosition);
      yPosition += lineHeight * 0.8;
    }
    if (insurance.subscriber_name) {
      pdf.text(`Subscriber Name: ${insurance.subscriber_name}`, margin, yPosition);
      yPosition += lineHeight * 0.8;
    }
  } else {
    pdf.text('No insurance information available.', margin, yPosition);
    yPosition += lineHeight * 0.8;
  }

  return yPosition + lineHeight;
};

const addAllergiesSection = (pdf, allergies, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('ALLERGIES', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (allergies && allergies.length > 0) {
    allergies.forEach((allergy, index) => {
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(10);
      pdf.setTextColor(178, 34, 34);
      pdf.text(`Allergy ${index + 1}:`, margin, yPosition);
      yPosition += lineHeight;

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);

      if (allergy.allergy_value) {
        const allergyLines = pdf.splitTextToSize(`Allergy: ${allergy.allergy_value}`, pageWidth - margin - 10);
        allergyLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      const allergyType = allergy.drug ? "Drug" : allergy.food ? "Food" : allergy.environment ? "Environment" : "Other";
      pdf.text(`Type: ${allergyType}`, margin + 5, yPosition);
      yPosition += lineHeight * 0.8;

      if (allergy.description) {
        const reactionLines = pdf.splitTextToSize(`Reaction: ${allergy.description}`, pageWidth - margin - 10);
        reactionLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (allergy.start_date) {
        const startDate = new Date(allergy.start_date).toLocaleDateString();
        pdf.text(`Start Date: ${startDate}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (allergy.added_by) {
        pdf.text(`Added by: ${allergy.added_by}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.5;
    });
  } else {
    pdf.text('No allergies data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addDiagnosisSection = (pdf, diagnosis, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('PATIENT DIAGNOSIS', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (diagnosis && diagnosis.length > 0) {
    diagnosis.forEach((item, index) => {
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(10);
      pdf.setTextColor(178, 34, 34);
      pdf.text(`Diagnosis ${index + 1}:`, margin, yPosition);
      yPosition += lineHeight;

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);

      if (item.code) {
        pdf.text(`Code: ${item.code}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (item.description) {
        const descLines = pdf.splitTextToSize(`Description: ${item.description}`, pageWidth - margin - 10);
        descLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (item.date) {
        const diagnosisDate = new Date(item.date).toLocaleDateString();
        pdf.text(`Diagnosis Date: ${diagnosisDate}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (item.added_by) {
        pdf.text(`Added by: ${item.added_by}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.5;
    });
  } else {
    pdf.text('No diagnosis data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addFamilyHistorySection = (pdf, familyHistory, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('FAMILY HISTORY', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (familyHistory && familyHistory.length > 0) {
    familyHistory.forEach((item, index) => {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }

      if (item.family_history) {
        const historyLines = pdf.splitTextToSize(`${index + 1}. ${item.family_history}`, pageWidth - margin - 10);
        historyLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (item.added_by) {
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Added by: ${item.added_by}`, margin + 5, yPosition);
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.3;
    });
  } else {
    pdf.text('No family history data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addMedicationsSection = (pdf, medications, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('ALL MEDICATIONS', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (medications && medications.length > 0) {
    medications.forEach((med, index) => {
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(10);
      pdf.setTextColor(25, 135, 84);
      pdf.text(`Medication ${index + 1}:`, margin, yPosition);
      yPosition += lineHeight;

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);

      if (med.medicine_name) {
        const medicineLines = pdf.splitTextToSize(`Medicine: ${med.medicine_name}`, pageWidth - margin - 10);
        medicineLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (med.start_date) {
        const startDate = new Date(med.start_date).toLocaleDateString();
        pdf.text(`Start Date: ${startDate}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (med.end_date) {
        const endDate = new Date(med.end_date).toLocaleDateString();
        pdf.text(`End Date: ${endDate}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (med.notes) {
        const notesLines = pdf.splitTextToSize(`Notes: ${med.notes}`, pageWidth - margin - 10);
        notesLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (med.added_by) {
        pdf.text(`Added by: ${med.added_by}`, margin + 5, yPosition);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.5;
    });
  } else {
    pdf.text('No medications data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addPastMedicalHistorySection = (pdf, pastMedicalHistory, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('PAST MEDICAL HISTORY', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (pastMedicalHistory && pastMedicalHistory.length > 0) {
    pastMedicalHistory.forEach((item, index) => {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }

      if (item.past_medical_history) {
        const historyLines = pdf.splitTextToSize(`${index + 1}. ${item.past_medical_history}`, pageWidth - margin - 10);
        historyLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (item.medical_history_date) {
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Date: ${item.medical_history_date}`, margin + 5, yPosition);
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.3;
    });
  } else {
    pdf.text('No past medical history data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addSurgicalHistorySection = (pdf, surgicalHistory, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('PAST SURGICAL HISTORY', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (surgicalHistory && surgicalHistory.length > 0) {
    surgicalHistory.forEach((item, index) => {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }

      if (item.past_surgical_history) {
        const historyLines = pdf.splitTextToSize(`${index + 1}. ${item.past_surgical_history}`, pageWidth - margin - 10);
        historyLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      if (item.surgery_date) {
        const surgeryDate = new Date(item.surgery_date).toLocaleDateString();
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Surgery Date: ${surgeryDate}`, margin + 5, yPosition);
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.3;
    });
  } else {
    pdf.text('No surgical history data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addSocialHistorySection = (pdf, socialHistory, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('SOCIAL HISTORY', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (socialHistory && socialHistory.length > 0) {
    socialHistory.forEach((item, index) => {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }

      const socialType = item.tobacco ? "Tobacco" : item.alcohol ? "Alcohol" : item.drugs ? "Drugs" : item.other ? "Other" : "Unknown";
      pdf.text(`${index + 1}. ${socialType}:`, margin, yPosition);
      yPosition += lineHeight * 0.8;

      if (item.social_history) {
        const historyLines = pdf.splitTextToSize(item.social_history, pageWidth - margin - 10);
        historyLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      yPosition += lineHeight * 0.3;
    });
  } else {
    pdf.text('No social history data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addVaccinationHistorySection = (pdf, vaccinationHistory, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('VACCINATION HISTORY', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (vaccinationHistory && vaccinationHistory.length > 0) {
    vaccinationHistory.forEach((item, index) => {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }

      if (item.vaccination_history) {
        pdf.text(`${index + 1}. ${item.vaccination_history}`, margin, yPosition);
        yPosition += lineHeight * 0.8;
      }

      if (item.vaccination_date) {
        const vaccinationDate = new Date(item.vaccination_date).toLocaleDateString();
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Date: ${vaccinationDate}`, margin + 5, yPosition);
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.3;
    });
  } else {
    pdf.text('No vaccination history data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

const addPatientGoalsSection = (pdf, patientGoals, margin, yPosition, pageWidth, pageHeight, lineHeight) => {
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = margin;
  }

  pdf.setFontSize(12);
  pdf.setTextColor(52, 73, 94);
  pdf.text('PATIENT GOALS', margin, yPosition);
  yPosition += lineHeight;

  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight;

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  if (patientGoals && patientGoals.length > 0) {
    patientGoals.forEach((item, index) => {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }

      if (item.patient_goals) {
        const goalsLines = pdf.splitTextToSize(`${index + 1}. ${item.patient_goals}`, pageWidth - margin - 10);
        goalsLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      yPosition += lineHeight * 0.3;
    });
  } else {
    pdf.text('No patient goals data available.', margin, yPosition);
    yPosition += lineHeight;
  }

  return yPosition + lineHeight;
};

export const generateLabResultsPDF = async (labResults, patientInfo = null, officeSetting = null) => {
  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const lineHeight = 7;
  let yPosition = margin;

  // Convert single result to array for consistent processing
  const results = Array.isArray(labResults) ? labResults : [labResults];

  // Header
  pdf.setFontSize(24);
  pdf.setTextColor(41, 128, 185);
  pdf.text('LABORATORY RESULTS REPORT', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += lineHeight * 2.5;

  // Two-column layout for Facility and Patient Information
  const leftColumnX = margin;
  const rightColumnX = pageWidth / 2 + 10;
  const columnWidth = (pageWidth - margin * 2 - 20) / 2;
  const sectionStartY = yPosition;

  // Facility Information (Left Column)
  if (officeSetting) {
    pdf.setFontSize(12);
    pdf.setTextColor(52, 73, 94);
    pdf.text('Facility Information', leftColumnX, yPosition);
    let facilityY = yPosition + lineHeight;

    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    // Helper function to check if data should be displayed
    const shouldDisplay = (data) => Array.isArray(data) && data[1] === true;

    if (officeSetting.facility_name && officeSetting.facility_name[0]) {
      pdf.text(`Name: ${officeSetting.facility_name[0]}`, leftColumnX, facilityY);
      facilityY += lineHeight * 0.8;
    }

    if (shouldDisplay(officeSetting.facility_address)) {
      const addressLines = pdf.splitTextToSize(`Address: ${officeSetting.facility_address[0]}`, columnWidth);
      addressLines.forEach(line => {
        pdf.text(line, leftColumnX, facilityY);
        facilityY += lineHeight * 0.8;
      });
    }

    if (shouldDisplay(officeSetting.city) || shouldDisplay(officeSetting.state) || shouldDisplay(officeSetting.zipcode)) {
      const cityStateZip = [];
      if (shouldDisplay(officeSetting.city)) cityStateZip.push(officeSetting.city[0]);
      if (shouldDisplay(officeSetting.state)) cityStateZip.push(officeSetting.state[0]);
      if (shouldDisplay(officeSetting.zipcode)) cityStateZip.push(officeSetting.zipcode[0]);
      
      if (cityStateZip.length > 0) {
        pdf.text(cityStateZip.join(', '), leftColumnX, facilityY);
        facilityY += lineHeight * 0.8;
      }
    }

    if (shouldDisplay(officeSetting.office_phone)) {
      pdf.text(`Phone: ${officeSetting.office_phone[0]}`, leftColumnX, facilityY);
      facilityY += lineHeight * 0.8;
    }

    if (shouldDisplay(officeSetting.office_fax)) {
      pdf.text(`Fax: ${officeSetting.office_fax[0]}`, leftColumnX, facilityY);
      facilityY += lineHeight * 0.8;
    }

    if (shouldDisplay(officeSetting.buisness_email)) {
      pdf.text(`Email: ${officeSetting.buisness_email[0]}`, leftColumnX, facilityY);
      facilityY += lineHeight * 0.8;
    }

    yPosition = Math.max(yPosition, facilityY);
  }

  // Patient Information (Right Column)
  if (patientInfo) {
    let patientY = sectionStartY;
    
    pdf.setFontSize(12);
    pdf.setTextColor(52, 73, 94);
    pdf.text('Patient Information', rightColumnX, patientY);
    patientY += lineHeight;

    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    // Patient Name
    const patientName = patientInfo.first_name && patientInfo.last_name 
      ? `${patientInfo.first_name} ${patientInfo.last_name}`
      : 'N/A';
    pdf.text(`Name: ${patientName}`, rightColumnX, patientY);
    patientY += lineHeight * 0.8;

    // Date of Birth and Age
    if (patientInfo.date_of_birth) {
      const dob = formatDateForPDF(patientInfo.date_of_birth, officeSetting);
      pdf.text(`DOB: ${dob}`, rightColumnX, patientY);
      patientY += lineHeight * 0.8;
      
      // Calculate age
      const today = new Date();
      const birthDate = new Date(patientInfo.date_of_birth);
      const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      pdf.text(`Age: ${age} y/o`, rightColumnX, patientY);
      patientY += lineHeight * 0.8;
    }

    // Gender
    if (patientInfo.gender) {
      pdf.text(`Gender: ${patientInfo.gender}`, rightColumnX, patientY);
      patientY += lineHeight * 0.8;
    }

    // Patient ID
    if (patientInfo.id) {
      pdf.text(`Patient ID: ${patientInfo.id}`, rightColumnX, patientY);
      patientY += lineHeight * 0.8;
    }

    // Phone
    if (patientInfo.patient_phone) {
      pdf.text(`Phone: ${patientInfo.patient_phone}`, rightColumnX, patientY);
      patientY += lineHeight * 0.8;
    }

    // Report Generation Date
    pdf.text(`Report Date: ${formatDateForPDF(new Date(), officeSetting)}`, rightColumnX, patientY);
    patientY += lineHeight * 0.8;

    yPosition = Math.max(yPosition, patientY);
  }

  yPosition += lineHeight * 1.5;

  // Draw separator line
  pdf.setDrawColor(52, 73, 94);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += lineHeight * 1.5;

  // Lab Results Section
  if (results && results.length > 0) {
    pdf.setFontSize(14);
    pdf.setTextColor(52, 73, 94);
    pdf.text('LABORATORY RESULTS', margin, yPosition);
    yPosition += lineHeight;

    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight * 1.5;

    results.forEach((result, index) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 100) {
        pdf.addPage();
        yPosition = margin;
      }

      // Result Header
      pdf.setFontSize(12);
      pdf.setTextColor(41, 128, 185);
      pdf.text(`Result #${index + 1}`, margin, yPosition);
      yPosition += lineHeight;

      // Result Details
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      // Lab Order ID
      if (result.lab_order_id) {
        pdf.text(`Lab Order ID: ${result.lab_order_id}`, margin, yPosition);
        yPosition += lineHeight * 0.8;
      }

      // Service/Lab Name
      if (result.service_name) {
        pdf.text(`Lab Name: ${result.service_name}`, margin, yPosition);
        yPosition += lineHeight * 0.8;
      }

      // Date and Time
      if (result.date_time) {
        const resultDate = new Date(result.date_time);
        pdf.text(`Date: ${resultDate.toLocaleDateString()}`, margin, yPosition);
        pdf.text(`Time: ${resultDate.toLocaleTimeString()}`, margin + 80, yPosition);
        yPosition += lineHeight * 0.8;
      }

      // Uploaded By
      if (result.added_by) {
        pdf.text(`Uploaded By: ${result.added_by}`, margin, yPosition);
        yPosition += lineHeight * 0.8;
      }

      yPosition += lineHeight * 0.5;

      // Description/Results
      if (result.description) {
        pdf.setFontSize(10);
        pdf.setTextColor(52, 73, 94);
        pdf.text('Results & Description:', margin, yPosition);
        yPosition += lineHeight;

        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        
        // Handle HTML content in description (strip HTML tags for PDF)
        let cleanDescription = result.description.replace(/<[^>]*>/g, '');
        // Replace HTML entities
        cleanDescription = cleanDescription.replace(/&nbsp;/g, ' ');
        cleanDescription = cleanDescription.replace(/&amp;/g, '&');
        cleanDescription = cleanDescription.replace(/&lt;/g, '<');
        cleanDescription = cleanDescription.replace(/&gt;/g, '>');
        
        const descriptionLines = pdf.splitTextToSize(cleanDescription, pageWidth - margin * 2);
        descriptionLines.forEach(line => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
        });
      }

      // File Information
      if (result.uploaded_file_encrypted_name) {
        yPosition += lineHeight * 0.5;
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text('File attached to this result', margin, yPosition);
        yPosition += lineHeight * 0.8;
      }

      // Add separator between results
      if (index < results.length - 1) {
        yPosition += lineHeight;
        pdf.setDrawColor(220, 220, 220);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += lineHeight * 1.5;
      } else {
        yPosition += lineHeight;
      }
    });
  } else {
    pdf.setFontSize(12);
    pdf.setTextColor(128, 128, 128);
    pdf.text('No laboratory results available.', margin, yPosition);
  }

  // Add page numbers
  const pageCount = pdf.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // Save the PDF
  const dateStamp = new Date().toISOString().split('T')[0];
  const patientName = patientInfo?.first_name && patientInfo?.last_name 
    ? `${patientInfo.first_name}_${patientInfo.last_name}`
    : 'Patient';
  const fileName = `Lab_Results_${patientName}_${dateStamp}.pdf`;
  
  pdf.save(fileName);
};



